import { ActionIcon, Box, Group, Loader, Menu, Stack, Text } from '@mantine/core';
import { IconDownload, IconX } from '@tabler/icons-react';
import moment from 'moment';
import { useState } from 'react';

import { EnvironmentCountries, EORIGIN_DATE_FORMAT_WITH_TIME, Report, ReportStatus } from '@e-origin/shared';

import { failedIcon, processingIcon, releasedIcon } from '@assets';
import { IconBox } from '@components';
import { useSettings } from '@hooks/useSettings';
import { getReportMessage, STATUS_MESSAGES } from './reportMessageUtils';

interface ReportMenuItemProps {
  report: Report;
  isLast: boolean;
  onDownload: () => void;
  onDelete: () => void;
}

const STATUS_ICONS = {
  [ReportStatus.IN_PROGRESS]: <IconBox icon={processingIcon} />,
  [ReportStatus.DONE]: <IconBox icon={releasedIcon} />,
  [ReportStatus.FAILED]: <IconBox icon={failedIcon} />,
};

const buildReportMessage = (report: Report, isNL: boolean) => {
  const { prefix, value } = getReportMessage(report, isNL);
  const statusMessage = STATUS_MESSAGES[report.status];

  return (
    <Stack gap="xs">
      <Text size="sm" lh={1.4}>
        {prefix}{' '}
        <Text span fw="bold">
          {value}
        </Text>{' '}
        {statusMessage}
      </Text>
      {report.status === ReportStatus.DONE && report.file?.fileName && <Text size="xs">{report.file.fileName}</Text>}
    </Stack>
  );
};

export const ReportMenuItem: React.FC<ReportMenuItemProps> = ({ report, isLast, onDownload, onDelete }) => {
  const { data: settings } = useSettings();
  const isNL = settings.country === EnvironmentCountries.NL;
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDownload = () => {
    try {
      setIsDownloading(true);
      onDownload();
    } catch (err) {
      // silent fail - toast handled
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = () => {
    setIsDeleting(true);
    onDelete();
  };

  return (
    <Box key={report._id}>
      <Group p="xs" justify="space-between" align="center">
        <Group>
          {STATUS_ICONS[report.status]}
          <Box maw={350}>{buildReportMessage(report, isNL)}</Box>
        </Group>
        <Stack>
          <Group gap="md" miw={40} justify="flex-end">
            {report.status === ReportStatus.DONE && (
              <>
                {isDownloading ? (
                  <Loader size={20} />
                ) : (
                  <ActionIcon variant="subtle">
                    <IconDownload size={20} onClick={handleDownload} />
                  </ActionIcon>
                )}
              </>
            )}
            {report.status !== ReportStatus.IN_PROGRESS && (
              <>
                {isDeleting ? (
                  <Loader size={20} />
                ) : (
                  <ActionIcon variant="subtle">
                    <IconX size={20} onClick={handleDelete} />
                  </ActionIcon>
                )}
              </>
            )}
          </Group>
          <Text size="xs">{report.createdAt ? moment(report.createdAt).format(EORIGIN_DATE_FORMAT_WITH_TIME) : ''}</Text>
        </Stack>
      </Group>
      {!isLast && <Menu.Divider />}
    </Box>
  );
};
